import logging from '@sstdev/lib_logging';
import { http } from 'lib_ui-services';
import { useEffect, useState } from 'react';
import useNetwork from './useNetwork';
import useProfileRole from './useProfileRole';

const _p = {
    http,
    useNetwork,
    useProfileRole,
    debounceMs: 200
};
export const _private = _p;

/**
 * Fetches aggregate (count/groupBy) data from the server via the rules-engine count_ path.
 * Returns null data when offline instead of attempting a local fallback.
 *
 * @param {string} namespace
 * @param {string} relation
 * @param {{
 *   aggregateType?: { aggregateType: 'count' },
 *   groupBy?: { propertyName: string, optionalNamespace?: string, optionalRelation?: string },
 *   groupByBoolean?: { propertyName: string, optionalNamespace?: string, optionalRelation?: string, trueLabel?: string, falseLabel?: string },
 *   includedValues?: { values: string[], propertyName: string, optionalNamespace?: string, optionalRelation?: string, allowUndefined?: boolean },
 *   [filterName: string]: object
 * }} requestFilters - Filter object compatible with getQueryUrl
 * @param {{keepPreviousData?: boolean}} options Keep the last result and its filters while a new request loads.
 * @returns {{ data: object[]|null, dataFilters?: object, isLoading: boolean, isOffline: boolean, error: Error|null }}
 */
export default function useServerAggregation(namespace, relation, requestFilters, { keepPreviousData = false } = {}) {
    const { isOnline } = _p.useNetwork();
    const [, roleIsReady] = _p.useProfileRole();
    const [state, setState] = useState({ data: null, isLoading: true, isOffline: false, error: null });

    // Serialize filters so useEffect can detect deep changes without referential equality
    const serializedFilters = JSON.stringify(requestFilters);
    const requestKey = JSON.stringify([namespace, relation, serializedFilters, isOnline, roleIsReady]);

    useEffect(() => {
        if (!isOnline) {
            // Preserve existing data so charts don't flash to loading if isOnline briefly bounces
            setState(prev => ({ ...prev, isLoading: false, isOffline: true }));
            return;
        }
        if (!roleIsReady) {
            // Online, but the user's role / data-rights are still syncing. Stay in the loading
            // state — NOT the offline indicator (consumers render a cloud-off icon on isOffline) —
            // until the role is ready, so the count reflects data rights. The roleIsReady dep
            // re-runs this effect once the role loads.
            setState({ data: null, isLoading: true, isOffline: false, error: null });
            return;
        }

        let cancelled = false;

        const timer = setTimeout(() => {
            if (cancelled) return;
            setState(prev => ({
                data: keepPreviousData ? prev.data : null,
                dataFilters: keepPreviousData ? prev.dataFilters : undefined,
                requestKey,
                isLoading: true,
                isOffline: false,
                error: null
            }));

            async function fetchData() {
                try {
                    // requestFilters is re-parsed from serializedFilters to avoid stale closure issues
                    const filters = JSON.parse(serializedFilters);
                    const url = _p.http.getQueryUrl(namespace, relation, filters);
                    const result = await _p.http.get(url);
                    if (!cancelled) {
                        // handle both { items: [...] } and raw array shapes from the server
                        const items = result?.items ?? (Array.isArray(result) ? result : []);
                        setState({ data: items, dataFilters: filters, requestKey, isLoading: false, isOffline: false, error: null });
                        cancelled = true;
                    }
                } catch (err) {
                    logging.error('[useServerAggregation] aggregate request failed', err);
                    if (!cancelled) {
                        setState({ data: null, requestKey, isLoading: false, isOffline: false, error: err });
                    }
                }
            }

            fetchData();
        }, _p.debounceMs);

        return () => {
            cancelled = true;
            clearTimeout(timer);
        };
    }, [namespace, relation, serializedFilters, isOnline, roleIsReady, keepPreviousData, requestKey]);

    // Filters change before the debounced request starts. Mark that render as
    // pending too, so callers never plot old counts against newly selected dates.
    const pending = isOnline && roleIsReady && state.requestKey !== requestKey;
    return {
        ...state,
        data: pending && !keepPreviousData ? null : state.data,
        isLoading: pending || state.isLoading,
        error: pending ? null : state.error
    };
}
